import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { SiteNav, SiteFooter } from "../components/SiteChrome";
import { trackPageView, trackAppStoreClick } from "../lib/analytics";
import { useSeo, APP_STORE_URL, ORG_SCHEMA, WEBSITE_SCHEMA, SOFTWARE_APP_SCHEMA } from "../lib/seo";
import "../styles/site.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Arw = () => (
  <svg className="arw" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6" /></svg>
);

const OCCASIONS = ["Casual", "Work", "Business casual", "Date night", "Weekend", "Travel"];
const VIBES = ["Minimal", "Streetwear", "Classic", "Cozy", "Bold"];

const CAPS = [
  { to: "/ai-virtual-try-on", k: "AI Virtual Try-On", h: "See it on you", p: "Visualize clothing and complete outfits on your own body before you wear or buy them." },
  { to: "/ai-stylist", k: "AI Stylist", h: "What to wear today", p: "Weather-aware, occasion-ready outfits built from the clothes you already own." },
  { to: "/digital-closet", k: "Digital Closet", h: "Your wardrobe, digitized", p: "Add, scan or import your clothes into a searchable, auto-tagged closet." },
  { to: "/spotted-a-look", k: "Spotted a Look", h: "See a look, make it yours", p: "Upload a photo, identify the pieces, and try them on individually or together." },
  { to: "/shop-and-try", k: "Shop & Try", h: "Try before you buy", p: "Bring in anything you find online and preview it on yourself first." },
  { to: "/how-it-works", k: "How It Works", h: "The full flow", p: "Create your closet, get styled, try it on, shop & try, and plan your looks." },
];

function OutfitDemo() {
  const [occasion, setOccasion] = useState("Casual");
  const [vibe, setVibe] = useState("Minimal");
  const [notes, setNotes] = useState("");
  const [photo, setPhoto] = useState(null);
  const [loading, setLoading] = useState(false);
  const [image, setImage] = useState(null);
  const [error, setError] = useState("");
  const [remaining, setRemaining] = useState(null);

  const onFile = (e) => {
    const f = e.target.files && e.target.files[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const max = 1024;
        let { width, height } = img;
        if (width > max || height > max) {
          const s = max / Math.max(width, height);
          width = Math.round(width * s); height = Math.round(height * s);
        }
        const c = document.createElement("canvas");
        c.width = width; c.height = height;
        c.getContext("2d").drawImage(img, 0, 0, width, height);
        setPhoto(c.toDataURL("image/jpeg", 0.85));
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(f);
  };

  const generate = async () => {
    setLoading(true); setError(""); setImage(null);
    try {
      const { data } = await axios.post(`${API}/generate-outfit`, { occasion, vibe, notes, photo });
      setImage(data.image);
      setRemaining(data.remaining);
    } catch (e) {
      setError(e?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="demo">
      <div className="demo-controls">
        <div className="demo-label">Occasion</div>
        <div className="chips">{OCCASIONS.map((o) => <button key={o} className={"chip" + (occasion === o ? " sel" : "")} onClick={() => setOccasion(o)} data-testid={`occ-${o}`}>{o}</button>)}</div>
        <div className="demo-label">Vibe</div>
        <div className="chips">{VIBES.map((v) => <button key={v} className={"chip" + (vibe === v ? " sel" : "")} onClick={() => setVibe(v === vibe ? "" : v)} data-testid={`vibe-${v}`}>{v}</button>)}</div>
        <div className="demo-label">Add a detail (optional)</div>
        <input className="demo-input" value={notes} maxLength={160} onChange={(e) => setNotes(e.target.value)} placeholder="e.g. with a beige trench coat" data-testid="demo-notes" />
        <div className="demo-label">Your photo (optional — see it on you)</div>
        {photo ? (
          <div className="demo-photo">
            <img src={photo} alt="Your uploaded reference" />
            <button className="chip" onClick={() => setPhoto(null)} data-testid="demo-photo-remove">Remove photo</button>
          </div>
        ) : (
          <label className="demo-file" data-testid="demo-photo-label">
            <input type="file" accept="image/*" onChange={onFile} hidden data-testid="demo-photo-input" />
            Upload a full-body photo
          </label>
        )}
        <div style={{ marginTop: 22 }}>
          <button className="btn" onClick={generate} disabled={loading} data-testid="demo-generate">{loading ? "Styling…" : <>{photo ? "See it on you" : "Generate the look"} <Arw /></>}</button>
        </div>
        {remaining !== null && !error && <div className="demo-meta">Free demo looks left today: {remaining}</div>}
        {error && <div className="demo-err" data-testid="demo-error">{error}</div>}
        <div className="demo-meta" style={{ opacity: .7 }}>AI-generated visualization · results vary · your photo isn't stored</div>
      </div>
      <div className="demo-canvas" data-testid="demo-canvas">
        {loading ? <div className="spinner" /> : image ? <img src={image} alt={`AI-generated ${occasion} outfit, watermarked C-STYL`} data-testid="demo-image" /> : <div className="demo-ph">{photo ? <>Generate to see the<br />look on your photo</> : <>Pick an occasion &amp; vibe,<br />then generate your look</>}</div>}
      </div>
    </div>
  );
}

export default function Landing() {
  useSeo({
    title: "C-STYL — AI Virtual Try-On & Personal Stylist | See Yourself Wearing It",
    description: "C-STYL is your AI personal stylist, digital closet and virtual try-on companion. See outfits on your own body, style looks from clothes you own, and try pieces before you buy.",
    path: "/",
    jsonLd: [ORG_SCHEMA, WEBSITE_SCHEMA, SOFTWARE_APP_SCHEMA],
  });

  useEffect(() => {
    trackPageView();
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const els = document.querySelectorAll("[data-reveal]");
    if (reduce) { els.forEach((el) => el.classList.add("in")); return; }
    const io = new IntersectionObserver((es) => es.forEach((e) => { if (e.isIntersecting) { e.target.classList.add("in"); io.unobserve(e.target); } }), { threshold: 0.15 });
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  return (
    <div className="site" data-testid="landing-page">
      <SiteNav />

      <header className="hero" data-testid="hero">
        <div className="wrap hero-grid">
          <div className="hero-copy">
            <span className="eyebrow"><span className="live-dot" aria-hidden="true"></span>AI Virtual Try-On · Personal Stylist</span>
            <h1 className="serif">See Yourself <span className="grad">Wearing It.</span></h1>
            <p className="sub">C-STYL is your AI personal stylist, digital closet, and virtual try-on companion. See how outfits could look on you, discover what to wear from the wardrobe you already own, and try pieces before you buy them.</p>
            <div className="cta">
              <a className="btn" href="#try" data-testid="hero-try">Try the AI Stylist <Arw /></a>
              <a className="btn ghost" href="#explore" data-testid="hero-explore">Explore C-STYL</a>
            </div>
            <p className="featline">AI Virtual Try-On • AI Stylist • Digital Closet • Daily Outfits • Shop &amp; Try</p>
          </div>
          <aside className="hud" data-testid="hero-hud" aria-label="Live status">
            <span className="hud-br tl" /><span className="hud-br tr" /><span className="hud-br bl" /><span className="hud-br br" />
            <div className="hud-head"><span><span className="live-dot green" /> Stylist online</span><span className="hud-ver">v1.0</span></div>
            <div className="hud-item"><div className="hud-row"><span>Weather sync</span><span>Local</span></div><div className="hud-bar"><i style={{ width: "100%" }} /></div></div>
            <div className="hud-item"><div className="hud-row"><span>Outfit match</span><span>99%</span></div><div className="hud-bar"><i style={{ width: "99%" }} /></div></div>
            <div className="hud-foot"><span>See it on you</span><span className="hud-ok">Ready</span></div>
          </aside>
        </div>
      </header>

      {/* Interactive AI demo */}
      <section id="try">
        <div className="wrap">
          <div className="section-head" data-reveal><span className="kicker">Try the AI · Live demo</span><h2 className="serif">Generate a look in seconds</h2><p>Pick an occasion and a vibe and watch C-STYL's AI style a complete outfit for you — a taste of what the app does with your own closet.</p></div>
          <div data-reveal><OutfitDemo /></div>
        </div>
      </section>

      {/* Capabilities overview */}
      <section id="explore" className="band-deep">
        <div className="wrap">
          <div className="section-head" data-reveal><span className="kicker">Explore C-STYL</span><h2 className="serif">Everything C-STYL does</h2><p>One app for deciding what to wear, organizing your wardrobe, and seeing outfits on yourself before you commit.</p></div>
          <div className="cap-grid">
            {CAPS.map((c, i) => (
              <Link to={c.to} className="cap linkcard" data-reveal key={i} data-testid={`cap-${i}`}>
                <div className="cap-top"><span className="kicker">{c.k}</span><span className="arrow-btn" aria-hidden="true"><svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M7 17L17 7M7 7h10v10" /></svg></span></div>
                <h3 className="serif">{c.h}</h3>
                <p>{c.p}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Pro */}
      <section id="pro">
        <div className="wrap">
          <div className="section-head" data-reveal><span className="kicker">C-STYL Pro</span><h2 className="serif">Unlock Unlimited Virtual Try-On</h2><p>New users get 3 complimentary virtual try-ons. C-STYL Pro includes unlimited virtual try-ons.</p></div>
          <div className="plans" data-reveal>
            <div className="plan"><div className="tag">Monthly</div><div className="price">$5.99</div><div className="per">per month</div></div>
            <div className="plan feat"><div className="tag">Annual · best value</div><div className="price">$69.99</div><div className="per">per year</div></div>
          </div>
          <div style={{ textAlign: "center", marginTop: 32 }} data-reveal><a className="btn" href={APP_STORE_URL} target="_blank" rel="noopener noreferrer" onClick={trackAppStoreClick} data-testid="pro-cta">Get C-STYL Pro <Arw /></a></div>
          <p style={{ color: "var(--silver)", marginTop: 16, fontSize: 13, textAlign: "center" }}>Subscriptions are purchased on the web through Stripe — no Apple or Google Play billing.</p>
        </div>
      </section>

      {/* Download */}
      <section id="get" className="cta-band">
        <div className="wrap" data-reveal>
          <span className="kicker" style={{ justifyContent: "center", display: "flex", marginBottom: 18 }}>Get the app</span>
          <h2 className="serif">See yourself wearing it.</h2>
          <p>Download C-STYL and get your first outfit — and your first try-on — in minutes.</p>
          <div className="store">
            <a className="pill" href={APP_STORE_URL} target="_blank" rel="noopener noreferrer" onClick={trackAppStoreClick} data-testid="app-store-badge"><span><small>Download on the</small><b>App Store</b></span></a>
            <span className="pill" data-testid="google-play-badge"><span><small>Coming soon on</small><b>Google Play</b></span></span>
          </div>
          <div style={{ marginTop: 22 }}><Link to="/faq" className="film-exit" data-testid="faq-link" style={{ color: "var(--silver)" }}>Read the FAQ →</Link></div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
